import { inject } from '@angular/core';
import { AuthApiActions } from '@iot-platform/auth';
import { LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { UserAccount } from '@iot-platform/models/common';
import { NotificationService } from '@iot-platform/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { PreferencesActions } from '../actions';

const loadUserPreferences$ = createEffect(
  (actions$ = inject(Actions), userPrefService = inject(UserPreferencesService)) =>
    actions$.pipe(
      ofType(PreferencesActions.loadUserPreferences),
      switchMap(() =>
        userPrefService.loadPreferences().pipe(
          map((user) => PreferencesActions.loadUserPreferencesSuccess({ user, preferences: user.preferences })),
          catchError((error) => of(PreferencesActions.loadUserPreferencesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const saveUserPreferences$ = createEffect(
  (actions$ = inject(Actions), userPrefService = inject(UserPreferencesService), storage = inject(LocalStorageService)) =>
    actions$.pipe(
      ofType(PreferencesActions.saveUserPreferences),
      switchMap(({ preferences }) =>
        userPrefService.saveUserPreferences(preferences).pipe(
          map((user: UserAccount) => {
            storage.set(LocalStorageKeys.STORAGE_USER_PREFERENCES, JSON.stringify(preferences));
            return PreferencesActions.saveUserPreferencesSuccess({ user, preferences });
          }),
          catchError((error) => of(PreferencesActions.saveUserPreferencesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const changeLanguage$ = createEffect(
  (actions$ = inject(Actions), translateService = inject(TranslateService)) =>
    actions$.pipe(
      ofType(PreferencesActions.changeLanguage),
      tap(({ lang }) => {
        // @ngx-translate 18: `use()` always emits `onLangChange` (no need to reset `currentLang`, now a read-only signal)
        if (lang && translateService.getLangs().includes(lang)) {
          translateService.use(lang);
        }
      })
    ),
  { functional: true, dispatch: false }
);

const displaySuccess$ = createEffect(
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(PreferencesActions.saveUserPreferencesSuccess),
      tap((action: Action) => {
        notificationService.displaySuccess(action.type);
      })
    ),
  { functional: true, dispatch: false }
);

const displayError$ = createEffect(
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(PreferencesActions.saveUserPreferencesFailure),
      tap((action) => notificationService.displayError(action))
    ),
  { functional: true, dispatch: false }
);

const pendingActions$ = createEffect(
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(PreferencesActions.saveUserPreferences),
      map(() => notificationService.showLoader())
    ),
  { functional: true, dispatch: false }
);

const completedActions$ = createEffect(
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(PreferencesActions.saveUserPreferencesSuccess, PreferencesActions.saveUserPreferencesFailure),
      tap(() => notificationService.hideLoader())
    ),
  { functional: true, dispatch: false }
);

const signInSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        AuthApiActions.signInSuccess,
        AuthApiActions.retrieveSsoSessionSuccess,
        AuthApiActions.signInWithSSOSuccess,
        AuthApiActions.retrieveSessionSuccess
      ),
      map(() => PreferencesActions.loadUserPreferences())
    ),
  { functional: true }
);

export const PreferencesEffects = {
  signInSuccess$,
  completedActions$,
  pendingActions$,
  displayError$,
  displaySuccess$,
  changeLanguage$,
  loadUserPreferences$,
  saveUserPreferences$
};
